import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { 
  ArrowRightIcon,
  DocumentTextIcon,
  UserGroupIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  HeartIcon,
  ClipboardDocumentCheckIcon,
  ChatBubbleLeftRightIcon,
  EyeIcon,
  CheckCircleIcon,
  SparklesIcon,
  HomeIcon
} from '@heroicons/react/24/outline';
import { ImageOptimizer } from '@/components/seo/ImageOptimizer';
import thriveCareHero from '@/assets/thrivecare-hero.png';
import avatarWoman1 from '@/assets/avatar-woman-1.jpg';
import avatarMan1 from '@/assets/avatar-man-1.jpg';
import avatarWoman2 from '@/assets/avatar-woman-2.jpg';

const ThriveCare = () => {
  const { openModal } = useEarlyAdopter();

  const handleGetStarted = () => {
    openModal('ThriveCare - Get Started');
  };

  const familyQuestions = [
    "What was done today?",
    "Were routines followed?",
    "Has anything changed?",
    "Is everything going as expected?"
  ];

  const featureCards = [
    {
      icon: DocumentTextIcon,
      title: "Documenting Care Visits",
      description: "Structured visit notes, time-stamped records, and summaries families can see."
    },
    {
      icon: ClipboardDocumentCheckIcon,
      title: "Aligning on Expectations",
      description: "Clear care plans that define routines, preferences, and priorities."
    },
    {
      icon: ChartBarIcon,
      title: "Tracking Meaningful Changes",
      description: "Condition-aware tools that help notice changes early."
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Reducing Back-and-Forth",
      description: "One shared communication stream instead of scattered messages."
    }
  ];

  const careTeamMembers = [
    "Independent caregivers",
    "Private aides",
    "Small care teams",
    "Families hiring help directly"
  ];

  const carePacks = [
    { label: "Dementia and cognitive change", icon: SparklesIcon },
    { label: "Mobility and fall prevention", icon: ShieldCheckIcon },
    { label: "Diabetes", icon: HeartIcon },
    { label: "Heart health", icon: HeartIcon },
    { label: "Post-hospital recovery", icon: ClipboardDocumentCheckIcon },
    { label: "Mood and emotional wellbeing", icon: EyeIcon }
  ];

  return (
    <>
      <EnhancedSEO 
        title="ThriveCare - Structure for Professional Care. Clarity for Families. | SeniorThrive"
        description="ThriveCare adds a professional layer to the SeniorThrive Home Operating System, giving families and caregivers one shared system for documentation, communication, and accountability."
        keywords="ThriveCare, professional caregiver tools, care documentation, caregiver coordination, senior care, home care management"
        canonical="https://seniorthrive.com/caregiver-coordination"
        breadcrumbs={[
          { name: "Home", url: "https://seniorthrive.com/" },
          { name: "ThriveCare", url: "https://seniorthrive.com/caregiver-coordination" }
        ]}
        schema={{
          type: "Service",
          name: "ThriveCare",
          description: "Professional caregiver add-on to the SeniorThrive Home Operating System",
          provider: "SeniorThrive"
        }}
      />
      
      {/* HERO SECTION */}
      <div className="container mx-auto px-4 py-20">
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-primary-glow/10 rounded-2xl"></div>
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-primary/20 to-transparent rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-radial from-primary/15 to-transparent rounded-full blur-2xl"></div>
          </div>
          
          <div className="relative max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center min-h-[600px] p-8 lg:p-16">
            {/* Content Side */}
            <div className="space-y-8 order-2 lg:order-1">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2 bg-primary/10 backdrop-blur-sm px-4 py-2 rounded-full border border-primary/30">
                  <span className="text-sm font-semibold text-primary uppercase tracking-wider">ThriveCare</span>
                </div>
              </div>
              
              <div className="space-y-6">
                <h1 className="text-4xl lg:text-5xl font-bold leading-hero">
                  Structure for Professional Care.{' '}
                  <span className="text-primary">Clarity for Families.</span>
                </h1>
                <p className="text-xl lg:text-2xl text-muted-foreground leading-relaxed">
                  Hiring a caregiver is an important step. What often gets overlooked is how care is communicated once it begins.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  ThriveCare adds a professional layer to the SeniorThrive Home Operating System, giving families and caregivers one shared system for documentation, communication, and accountability.
                </p>
              </div>
              
              {/* Social proof */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex -space-x-2">
                  <img src={avatarWoman1} alt="ThriveCare user" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
                  <img src={avatarMan1} alt="ThriveCare user" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
                  <img src={avatarWoman2} alt="ThriveCare user" className="w-8 h-8 rounded-full border-2 border-background object-cover" />
                </div>
                <span>Join families building clarity with professional care</span>
              </div>
              
              {/* CTA Section */}
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button size="lg" variant="default" onClick={handleGetStarted}>
                    Get Started with ThriveFamily
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground">
                  ThriveCare is available as an add-on to ThriveFamily.
                </p>
              </div>
            </div>
            
            {/* Image Side */}
            <div className="order-1 lg:order-2">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-tr from-primary/30 to-primary-glow/20 rounded-2xl transform rotate-2"></div>
                <div className="relative">
                  <ImageOptimizer 
                    src={thriveCareHero} 
                    alt="Professional caregiver coordinating with family through ThriveCare" 
                    imgClassName="w-full h-[400px] lg:h-[500px] object-cover rounded-2xl shadow-2xl transform -rotate-1 hover:rotate-0 transition-transform duration-500"
                    sizes="(min-width: 1024px) 600px, 100vw"
                    width={600}
                    height={500}
                    quality={85}
                    priority={true}
                  />
                  <div className="absolute -bottom-4 -right-4 bg-background/95 backdrop-blur-sm px-6 py-3 rounded-full shadow-lg border border-primary/20">
                    <span className="text-sm font-medium text-primary">✓ Shared & Structured</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="pb-20 px-4">
        <div className="container mx-auto">

          {/* SECTION 1: THE PROBLEM - When Care Enters the Home */}
          <div className="py-16 mb-8">
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-amber-100 dark:bg-amber-900/30 rounded-xl">
                    <HeartIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <h2 className="text-2xl lg:text-3xl font-bold text-foreground">When Care Enters the Home, Clarity Matters</h2>
                </div>
                
                <div className="space-y-4">
                  <p className="text-lg text-foreground leading-relaxed font-medium">
                    Once a caregiver is involved, families start asking questions. Not because they distrust the caregiver, but because information lives in texts, notebooks, and memory.
                  </p>
                </div>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  {familyQuestions.map((question, i) => (
                    <div key={i} className="flex items-center gap-3 bg-muted/50 p-4 rounded-xl border border-border/50">
                      <span className="text-2xl text-amber-500">?</span>
                      <span className="text-foreground italic text-sm">{question}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-primary/5 p-6 rounded-xl border-l-4 border-primary">
                  <p className="text-foreground font-medium text-lg">
                    ThriveCare replaces scattered communication with structured visibility.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 2: WHAT THRIVECARE ADDS */}
          <div className="py-16">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">What ThriveCare Adds</h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  ThriveCare integrates directly into the existing household dashboard. It creates one shared system for everyone involved in care.
                </p>
              </div>
              
              <div className="grid md:grid-cols-2 gap-8">
                {featureCards.map((card, idx) => (
                  <div key={idx} className="bg-background/50 backdrop-blur-sm p-8 rounded-2xl border border-primary/10 hover:border-primary/30 hover:shadow-lg transition-all">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        <card.icon className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                    <h3 className="text-xl font-bold text-foreground mb-3">{card.title}</h3>
                    <p className="text-muted-foreground">{card.description}</p>
                  </div>
                ))}
              </div>
              
              <p className="text-center text-muted-foreground mt-8">
                Care becomes easier to coordinate because everyone works from the same information.
              </p>
            </div>
          </div>

          {/* SECTION 3: BUILT FOR REAL CARE TEAMS */}
          <div className="py-16">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Built for Real Care Teams</h2>
                <p className="text-xl text-muted-foreground leading-relaxed">
                  ThriveCare does not replace the caregiver. It strengthens how care is understood and shared.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {careTeamMembers.map((member, i) => (
                  <div key={i} className="flex items-center gap-3 bg-background/50 backdrop-blur-sm p-5 rounded-xl border border-primary/10 hover:border-primary/30 transition-colors">
                    <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-foreground font-medium">{member}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* SECTION 4: CARE PLANS & CONDITION GUIDANCE */}
          <div className="py-16">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-10">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Care Plans & Condition Guidance</h2>
                <p className="text-xl text-muted-foreground leading-relaxed mb-2">
                  ThriveCare includes structured Care Plans that define daily routines, preferences, risk priorities, and family expectations.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 items-start">
                {/* Care Plan Details */}
                <div className="bg-background/50 backdrop-blur-sm p-8 rounded-2xl border border-primary/10">
                  <h3 className="text-xl font-bold text-foreground mb-6">What Care Plans Define</h3>
                  <div className="space-y-3">
                    {["Daily routines", "Preferences", "Risk priorities", "Family expectations"].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border border-border/30">
                        <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-foreground font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Care Packs */}
                <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 rounded-2xl border-2 border-primary/20">
                  <div className="flex items-center gap-2 mb-6">
                    <span className="bg-primary text-primary-foreground text-xs font-semibold px-3 py-1 rounded-full">Optional</span>
                    <h3 className="text-xl font-bold text-foreground">Condition-Specific Care Packs</h3>
                  </div>
                  <div className="space-y-3">
                    {carePacks.map((pack, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-primary/20">
                        <pack.icon className="w-5 h-5 text-primary flex-shrink-0" />
                        <span className="text-foreground text-sm">{pack.label}</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground mt-4">
                    This helps caregivers capture the right information, not just more information.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* SECTION 5: HOW IT FITS INTO SENIORTHRIVE */}
          <div className="py-16">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">How It Fits Into SeniorThrive</h2>
              <p className="text-xl text-muted-foreground leading-relaxed mb-10">
                ThriveCare is not a separate system. When professional care begins, the system expands.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                {[
                  { name: "ThriveMax", sub: "Personal foundation", icon: HomeIcon },
                  { name: "ThriveFamily", sub: "Coordination layer", icon: UserGroupIcon },
                  { name: "ThriveCare", sub: "Professional care layer", icon: ShieldCheckIcon, highlight: true }
                ].map((tier, i) => (
                  <div key={i} className={`p-6 rounded-2xl border-2 transition-all ${
                    tier.highlight 
                      ? 'bg-gradient-to-br from-primary/5 to-primary/10 border-primary/30 shadow-lg' 
                      : 'bg-background/50 border-border/50 hover:border-primary/20'
                  }`}>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                      tier.highlight ? 'bg-primary/20' : 'bg-muted'
                    }`}>
                      <tier.icon className={`w-6 h-6 ${tier.highlight ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <h3 className="font-bold text-foreground mb-1">{tier.name}</h3>
                    <p className="text-sm text-muted-foreground">{tier.sub}</p>
                  </div>
                ))}
              </div>
              
              <div className="flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <span>No new platform.</span>
                <span>No fragmented tools.</span>
                <span>No duplicated communication.</span>
              </div>
            </div>
          </div>

          {/* SECTION 6: PRICING */}
          <div className="py-16">
            <div className="max-w-3xl mx-auto">
              <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-10 rounded-2xl border-2 border-primary/20 text-center">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Pricing</h2>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-primary">$15</span>
                  <span className="text-xl text-muted-foreground">/month per caregiver seat</span>
                </div>
                <p className="text-lg text-muted-foreground mb-8">
                  ThriveCare is an add-on to the ThriveFamily subscription.
                </p>
                
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                  {[
                    "Add or remove caregivers anytime",
                    "Cancel anytime",
                    "Paid by the family"
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2 justify-center p-3 rounded-lg bg-background/50 border border-primary/20">
                      <CheckCircleIcon className="w-5 h-5 text-primary flex-shrink-0" />
                      <span className="text-foreground text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
                
                <p className="text-muted-foreground">
                  This allows families to scale support only when care is needed.
                </p>
              </div>
            </div>
          </div>

          {/* FINAL CTA */}
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-glow to-primary/80"></div>
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-primary-foreground/20 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-radial from-primary-foreground/15 to-transparent rounded-full blur-2xl"></div>
            </div>
            
            <div className="relative text-center p-12 lg:p-16 text-primary-foreground">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Care Works Better When Everyone Shares the Same Understanding
              </h2>
              <div className="space-y-2 mb-8 text-lg opacity-95 max-w-2xl mx-auto">
                <p>ThriveCare is not about oversight.</p>
                <p>It is about cooperation. Shared context. Fewer assumptions. Less stress.</p>
                <p className="font-medium">When communication improves, care improves.</p>
              </div>
              
              <div className="flex flex-wrap items-center justify-center gap-6 mb-8 text-primary-foreground/90">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">Add-On to ThriveFamily</span>
                </div>
                <div className="flex items-center gap-2">
                  <HeartIcon className="w-5 h-5" />
                  <span className="text-sm font-medium">$15/mo per Caregiver</span>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  variant="secondary"
                  className="text-lg px-8 py-4 bg-white text-primary hover:bg-white/95 shadow-lg hover:shadow-xl transition-all group font-semibold"
                  onClick={handleGetStarted}
                >
                  Get Started with ThriveFamily
                  <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              
              <p className="mt-6 text-sm opacity-80">
                Add ThriveCare anytime inside the app.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ThriveCare;
